import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { searchOdoo } from '@adornis/odoo/search.js';

const TAG_CACHE_TIME = 6 * 60 * 60 * 1000; // 6 hours

export interface OdooTag {
  id: number;
  name: string;
  display_name: string;
}

interface OdooTagCacheDoc {
  _id: string;
  key: string;
  value: string;
  expiresAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

function tagListCacheKey(type: 'partner' | 'ticket') {
  return (suffix: string): string => {
    return `tag-list:${type}:${suffix}`;
  };
}

// ensure indexes on startup
void (async () => {
  logger.info('TagService::Ensuring indexes for tag cache');
  const collection = await getRawCollection<OdooTagCacheDoc>('OdooTagCache');
  await collection.createIndex({ key: 1 }, { unique: true });
  await collection.createIndex({ expiresAt: 1 }, { expireAfterSeconds: 0 });
})();

function tagNameCacheKey(type: 'partner' | 'ticket') {
  return (tagname: string): string => {
    return `tag-name:${type}:${tagname}`;
  };
}

function tagIdCacheKey(type: 'partner' | 'ticket') {
  return (tagId: number): string => {
    return `tag-id:${type}:${tagId}`;
  };
}

/**
 * TODO currently disabled caching
 */
export async function getOdooTagsByNameList(type: 'partner' | 'ticket', tagnames: string[]): Promise<OdooTag[]> {
  logger.info({ type, tagnames }, 'TagService::Fetching tags by name list');
  // if tagnames contains "Antworten auf Zahlungserinnerungen" but not yet Anwers to payment reminders, add Anwers to payment reminders to the array
  const inputNames = [...tagnames];
  if (
    inputNames.includes('Antworten auf Zahlungserinnerungen') &&
    !inputNames.includes('Anwers to payment reminders')
  ) {
    inputNames.push('Anwers to payment reminders');
  }

  const collection = await getRawCollection<OdooTagCacheDoc>('OdooTagCache');

  const uniqueNames = [...new Set(inputNames)].filter(Boolean);
  const keys = uniqueNames.map(tagNameCacheKey(type));
  const cachedDocs = await collection.find({ key: { $in: keys } }).toArray();
  const cachedByName = new Map<string, OdooTag>();

  for (const doc of cachedDocs) {
    if (!doc?.key || !doc?.value) continue;
    const name = doc.key.replace(new RegExp(`^tag-name:${type}:`), '');
    try {
      const tag = JSON.parse(doc.value) as OdooTag;
      if (tag?.name) cachedByName.set(name, tag);
    } catch {
      // ignore broken cache entries
    }
  }

  const missingNames = uniqueNames.filter(n => !cachedByName.has(n));

  if (missingNames.length) {
    logger.info({ missingNames }, 'TagService::Fetching missing tags by name list');
    const domain: any[] = [[['name', 'in', tagnames]]];
    const fields = ['id', 'name', 'display_name'];
    const tags = await searchOdoo(type === 'ticket' ? 'helpdesk.tag' : 'res.partner.category', {
      domain,
      fields,
      context: { lang: 'de_DE' },
    });

    const collection = await getRawCollection<OdooTagCacheDoc>('OdooTagCache');
    const expiresAt = new Date(new Date().getTime() + TAG_CACHE_TIME);

    if (Array.isArray(tags) && tags.length) {
      await collection.bulkWrite(
        tags
          .filter((t: OdooTag) => !!t?.name)
          .map((t: OdooTag) => {
            const key = tagNameCacheKey(type)(t.name);
            return {
              updateOne: {
                filter: { key },
                update: {
                  $set: {
                    key,
                    value: JSON.stringify(t),
                    expiresAt,
                    updatedAt: new Date(),
                  },
                  $setOnInsert: {
                    _id: A.getGloballyUniqueID(),
                    createdAt: new Date(),
                  },
                },
                upsert: true,
              },
            };
          }),
        { ordered: false },
      );
    }

    return tags;
  }

  return uniqueNames.map(n => cachedByName.get(n)).filter(Boolean) as OdooTag[];
}

/**
 * TODO currently disabled caching
 */
export async function getOdooTagsByIdList(type: 'partner' | 'ticket', tagIds: number[]): Promise<OdooTag[]> {
  logger.info({ type, tagIds }, 'TagService::Fetching tags by id list');
  const collection = await getRawCollection<OdooTagCacheDoc>('OdooTagCache');

  const uniqueIds = [...new Set(tagIds)].filter(id => typeof id === 'number' && Number.isFinite(id));
  const keys = uniqueIds.map(tagIdCacheKey(type));
  const cachedDocs = await collection.find({ key: { $in: keys } }).toArray();
  const cachedById = new Map<number, OdooTag>();

  for (const doc of cachedDocs) {
    if (!doc?.key || !doc?.value) continue;
    const idString = doc.key.replace(new RegExp(`^tag-id:${type}:`), '');
    const id = Number(idString);
    if (!Number.isFinite(id)) continue;

    try {
      const tag = JSON.parse(doc.value) as OdooTag;
      if (typeof tag?.id === 'number') cachedById.set(id, tag);
    } catch {
      // ignore broken cache entries
    }
  }

  const missingIds = uniqueIds.filter(id => !cachedById.has(id));

  if (missingIds.length) {
    logger.info({ missingIds }, 'TagService::Fetching missing tags by id list');
    const domain: any[] = [[['id', 'in', missingIds]]];
    const fields = ['id', 'name', 'display_name'];
    const tags = await searchOdoo('res.partner.category', { domain, fields, context: { lang: 'de_DE' } });

    const now = new Date();
    const expiresAt = new Date(now.getTime() + TAG_CACHE_TIME);

    if (Array.isArray(tags) && tags.length) {
      await collection.bulkWrite(
        tags
          .filter((t: OdooTag) => typeof t?.id === 'number')
          .map((t: OdooTag) => {
            const key = tagIdCacheKey(type)(t.id);
            return {
              updateOne: {
                filter: { key },
                update: {
                  $set: {
                    key,
                    value: JSON.stringify(t),
                    expiresAt,
                    updatedAt: now,
                  },
                  $setOnInsert: {
                    _id: A.getGloballyUniqueID(),
                    createdAt: now,
                  },
                },
                upsert: true,
              },
            };
          }),
        { ordered: false },
      );
    }

    return tags;
  }

  return tagIds.map(id => cachedById.get(id)).filter(Boolean) as OdooTag[];
}

export async function getNonNdiTicketTagIds(): Promise<number[]> {
  logger.info('TagService::Fetching non-NDI ticket tag ids');
  const collection = await getRawCollection<OdooTagCacheDoc>('OdooTagCache');
  const cacheKey = tagListCacheKey('ticket')('non-ndi-ids');
  const cachedDoc = await collection.findOne({ key: cacheKey });

  if (cachedDoc?.value) {
    try {
      const cachedTagIds = JSON.parse(cachedDoc.value) as unknown;
      if (
        Array.isArray(cachedTagIds) &&
        cachedTagIds.every(tagId => typeof tagId === 'number' && Number.isFinite(tagId))
      ) {
        return cachedTagIds;
      }
    } catch {
      // ignore broken cache entry
    }
  }

  const ndiTags = await getOdooTagsByNameList('ticket', ['NDI']);
  const ndiTagId = ndiTags[0]?.id;
  if (!ndiTagId) {
    return [];
  }

  const nonNdiTags = await searchOdoo('helpdesk.tag', {
    domain: [[['id', '!=', ndiTagId]]],
    fields: ['id'],
    context: { lang: 'de_DE' },
  });
  const nonNdiTagIds = nonNdiTags
    .map(tag => tag.id)
    .filter((tagId): tagId is number => typeof tagId === 'number' && Number.isFinite(tagId));

  const now = new Date();
  const expiresAt = new Date(now.getTime() + TAG_CACHE_TIME);
  await collection.updateOne(
    { key: cacheKey },
    {
      $set: {
        key: cacheKey,
        value: JSON.stringify(nonNdiTagIds),
        expiresAt,
        updatedAt: now,
      },
      $setOnInsert: {
        _id: A.getGloballyUniqueID(),
        createdAt: now,
      },
    },
    { upsert: true },
  );

  return nonNdiTagIds;
}

export function removePseudoTags(tagnames: string[]) {
  const tagNamesCheckAutoreply = addArchiveIfAutoreply(tagnames);
  return tagNamesCheckAutoreply.filter(tag => !tag.includes(':') && !tag.includes('my_data'));
}

function addArchiveIfAutoreply(tagnames: string[]) {
  if (tagnames.includes('Auto-Reply')) {
    if (!tagnames.includes('Archive')) return [...tagnames, 'Archive'];
  }
  return tagnames;
}
